import  LinkedinRequestsRepository  from "infra/repositories/linkedinrequests-repository";
import { useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import { LinkedinRequestsInputFilters } from "domain/models/linkedinrequests-input-filter";
import { Logger } from "infra/logger/logger";
import { AxiosError } from "axios";
import { LinkedinRequestsPayload, UpdateInput } from "domain/entities/linkedinrequests";

export const useLinkedinRequestsByFilter = (filter: LinkedinRequestsInputFilters) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["linkedinrequests", filter],
    queryFn: async () => {
        try {
            const result = await LinkedinRequestsRepository.getAll(filter);
            return result;
        } catch (error: any) {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
            );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
});
};

export const useLinkedinRequestsById = (id: string) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["linkedinrequests", id],
        queryFn: async () => {
            try {
                if (!id) throw new Error("ID inválido");
                const result = await LinkedinRequestsRepository.getById(id);
                return result;
            } catch (error: any) {
                if (error instanceof AxiosError) {
                logger.error(error.response?.data.metadata.errors[0],
                error.response?.data?.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
});
};

export const useCreateLinkedinRequestsMutation = () => {
    const logger = Logger.getInstance();
    const queryLinkedinRequests = useQueryClient();

    return useMutation({
        mutationFn: async (requests: LinkedinRequestsPayload) => {
            const result = await LinkedinRequestsRepository.create(requests);
            return result;
        },
        onSuccess: (requests) => {
            queryLinkedinRequests.invalidateQueries({ queryKey: ["linkedinrequests"] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    const customError = new Error("Erro ao criar um pedido do LinkedIn");
                    (customError as any).errors = errors;
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
            }
        }
    });
};

export const useDeleteLinkedinRequestsMutation = () => {
    const logger = Logger.getInstance();
    const queryLinkedinRequests = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!id) throw new Error("ID inválido para exclusão");
            const result = await LinkedinRequestsRepository.delete(id);
            return result;
        },
        onSuccess: (requests) => {
            queryLinkedinRequests.removeQueries({ queryKey: ["linkedinrequests"] });
            queryLinkedinRequests.invalidateQueries({ queryKey: ["linkedincards"] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                const backendMessage =
                    error.response?.data?.metadata?.errors?.[0] ??
                    error.response?.data?.error;
                logger.error(backendMessage, error.response?.data);
                throw new Error(backendMessage ?? error.message);
            }
            throw error;
        }
    });
};

export const useUpdateLinkedinRequestsMutation = () => {
  const logger = Logger.getInstance();
  const queryLinkedinRequests = useQueryClient();

  return useMutation({
    mutationFn: async (newdata: UpdateInput) => {
      const result = await LinkedinRequestsRepository.update(newdata);
      return result;
    },
    onSuccess: () => {
      queryLinkedinRequests.invalidateQueries({ queryKey: ["linkedinrequests"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};
